import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Phone, BookOpen, CalendarClock, ClipboardList, CheckSquare, FileCheck2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Loading, PageTitle, Empty } from "@/components/Common";
import { ESTADOS_PLAN } from "@/components/PlanificacionForm";
import { DIAS } from "@/components/HorarioSemanal";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";

const Bloque = ({ titulo, children, testId }) => (
  <section className="mt-6" data-testid={testId}>
    <h3 className="text-xs font-bold uppercase tracking-[0.18em] text-muted-foreground">{titulo}</h3>
    <div className="mt-3 space-y-2 text-sm">{children}</div>
  </section>
);

export default function CoordDocentes() {
  const [docentes, setDocentes] = useState(null);
  const [detalle, setDetalle] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/coordinacion/docentes").then((r) => setDocentes(r.data)).catch(() => setDocentes([]));
  }, []);

  const abrir = async (d) => {
    setDetalle({ cargando: true });
    try {
      const { data } = await api.get(`/coordinacion/docentes/${d.id}`);
      setDetalle(data);
    } catch (e) {
      toast.error(apiError(e));
      setDetalle(null);
    }
  };

  if (!docentes) return <Loading />;

  return (
    <div data-testid="coord-docentes">
      <PageTitle title="Docentes"
                 subtitle="Carga académica, actividades publicadas y estado de las planificaciones de cada docente." />

      {docentes.length === 0 ? (
        <Empty testId="sin-docentes">Todavía no hay docentes registrados.</Empty>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="lista-docentes">
          {docentes.map((d) => (
            <button key={d.id} onClick={() => abrir(d)} data-testid={`docente-${d.id}`}
                    className="lift rounded-lg border border-border/60 bg-card p-6 text-left">
              <h3 className="font-display text-base font-bold">{d.nombre}</h3>
              <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                <Mail className="h-3 w-3" /> {d.email}
              </p>
              {d.telefono && (
                <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                  <Phone className="h-3 w-3" /> {d.telefono}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-1.5">
                {d.materias.length === 0 && <Badge variant="outline">Sin materias</Badge>}
                {d.materias.map((m) => (
                  <Badge key={m.id} variant="outline" className="text-[10px]">{m.nombre} · {m.grado}</Badge>
                ))}
              </div>
              <div className="mt-5 grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                <span className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /> {d.horas_semanales} h/sem</span>
                <span className="flex items-center gap-1"><ClipboardList className="h-3 w-3" /> {d.total_tareas} tareas</span>
                <span className="flex items-center gap-1"><BookOpen className="h-3 w-3" /> {d.total_notas} notas</span>
                <span className="flex items-center gap-1"><CheckSquare className="h-3 w-3" /> {d.total_asistencias} asist.</span>
              </div>
              {d.planificaciones.enviada > 0 && (
                <Badge className="mt-4" data-testid={`pendiente-plan-${d.id}`}>
                  {d.planificaciones.enviada} planificación(es) por revisar
                </Badge>
              )}
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!detalle} onOpenChange={(o) => !o && setDetalle(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto" data-testid="modal-docente">
          <DialogHeader>
            <DialogTitle className="font-display">
              {detalle?.cargando ? "Cargando docente…" : detalle?.docente?.nombre}
            </DialogTitle>
            <DialogDescription>
              {detalle?.cargando ? "Consultando carga académica y planificaciones."
                : `${detalle?.materias?.length || 0} materia(s) · ${detalle?.horas_semanales || 0} horas semanales`}
            </DialogDescription>
          </DialogHeader>
          {detalle?.cargando ? <Loading /> : detalle && (
            <>
              <Bloque titulo="Horario asignado" testId="detalle-horario">
                {detalle.horario.length === 0
                  ? <p className="text-xs text-muted-foreground">Sin bloques de horario asignados.</p>
                  : detalle.horario.map((b) => (
                    <p key={b.id} className="flex flex-wrap gap-2 text-xs">
                      <span className="font-semibold">{DIAS[b.dia]}</span>
                      <span className="font-mono">{b.hora_inicio}–{b.hora_fin}</span>
                      <span>{b.materia_nombre} · {b.grado}{b.aula ? ` · ${b.aula}` : ""}</span>
                    </p>
                  ))}
              </Bloque>

              <Bloque titulo="Planificaciones" testId="detalle-planificaciones">
                {detalle.planificaciones.length === 0
                  ? <p className="text-xs text-muted-foreground">Este docente no ha registrado planificaciones.</p>
                  : detalle.planificaciones.map((p) => (
                    <div key={p.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 p-3">
                      <div>
                        <p className="text-sm font-medium">{p.titulo}</p>
                        <p className="text-xs text-muted-foreground">
                          {p.materia_nombre} · {p.periodo}{p.puntuacion != null ? ` · ${p.puntuacion}/100` : ""}
                        </p>
                      </div>
                      <Badge variant={ESTADOS_PLAN[p.estado]?.variant}>{ESTADOS_PLAN[p.estado]?.label}</Badge>
                    </div>
                  ))}
                <Button size="sm" variant="outline" className="mt-2 rounded-full"
                        data-testid="revisar-planificaciones-docente"
                        onClick={() => navigate("/coordinacion/planificaciones",
                                                { state: { docenteId: detalle.docente.id } })}>
                  <FileCheck2 className="mr-2 h-3 w-3" /> Revisar planificaciones
                </Button>
              </Bloque>

              <Bloque titulo="Tareas y actividades recientes" testId="detalle-tareas">
                {detalle.tareas.length === 0
                  ? <p className="text-xs text-muted-foreground">Sin tareas publicadas.</p>
                  : detalle.tareas.map((t) => (
                    <p key={t.id} className="text-xs">
                      <span className="font-semibold">{t.titulo}</span> · {t.materia_nombre} ·
                      {" "}entrega {String(t.fecha_entrega).slice(0, 10)} · {t.entregas} entrega(s)
                    </p>
                  ))}
              </Bloque>

              <Bloque titulo="Calificaciones publicadas" testId="detalle-notas">
                {detalle.notas.length === 0
                  ? <p className="text-xs text-muted-foreground">Sin calificaciones publicadas.</p>
                  : detalle.notas.map((n) => (
                    <p key={n.id} className="text-xs">
                      {n.estudiante_nombre} · {n.materia_nombre} · {n.periodo}:
                      <span className="font-semibold"> {n.nota}</span>
                    </p>
                  ))}
              </Bloque>

              <Bloque titulo="Asistencia registrada" testId="detalle-asistencia">
                {detalle.asistencia.length === 0
                  ? <p className="text-xs text-muted-foreground">Sin asistencia registrada.</p>
                  : detalle.asistencia.map((a) => (
                    <p key={`${a.fecha}-${a.materia_nombre}`} className="text-xs">
                      {a.fecha} · {a.materia_nombre}: {a.presente} presentes, {a.tardanza} tardanzas,
                      {" "}{a.ausente} ausentes, {a.excusa} excusas
                    </p>
                  ))}
              </Bloque>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
